<template>
  <div class="roles-setting">
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Roles del sistema</custom-title>
      </template>

      <template #right>
        <el-button
          type="primary"
          size="default"
          :icon="IconAdd"
          @click="openCreateModal"
        >
          Agregar Rol
        </el-button>
      </template>
    </main-buttons-bar>
    <el-table
      :data="rolList"
      style="width: 100%; margin-top: 20px"
      border
    >
      <el-table-column
        prop="Descripcion"
        label="Rol"
      />

      <el-table-column
        label="Sistema"
        width="120"
      >
        <template #default="{ row }">
          <el-tag :type="row.EsSistema ? 'warning' : 'info'">
            {{ row.EsSistema ? 'Sistema' : 'Custom' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Activo"
        width="100"
      >
        <template #default="{ row }">
          <el-switch
            v-model="row.Activo"
            disabled
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Acciones"
        width="180"
        align="center"
      >
        <template #default="{ row }">
          <el-button
            size="small"
            type="primary"
            :disabled="row.EsSistema"
            @click="editRole(row)"
          >
            Editar
          </el-button>

          <el-button
            size="small"
            type="danger"
            :disabled="row.EsSistema"
            @click="deleteRole(row)"
          >
            Eliminar
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-dialog
      v-model="createModalVisible"
      :title="mode === 'edit' ? 'Editar rol' : 'Nuevo rol'"
      width="800px"
      destroy-on-close
    >
      <el-form
        ref="roleFormRef"
        :model="newRole"
        :rules="roleRules"
        label-position="top"
      >
        <el-row :gutter="16">
          <el-col :span="10">
            <el-form-item
              label=""
              prop="Descripcion"
            >
              <custom-input
                v-model="newRole.Descripcion"
                label="Nombre del rol"
                :spanColumn="24"
                :settings="{ placeholder: 'Ingresar nombre del rol' }"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4">
            <custom-switch
              v-model="newRole.Activo"
              :span-column="24"
              label="Activo"
            />
          </el-col>
        </el-row>

        <el-divider />

        <div class="custom-label">Permisos por módulo</div>

        <el-table
          :data="modulesMatrix"
          border
          size="small"
          max-height="400"
        >
          <el-table-column
            prop="label"
            label="Módulo"
          />

          <el-table-column
            label="Visible"
            width="120"
            align="center"
          >
            <template #default="{ row }">
              <el-switch
                v-model="row.visible"
                @change="(v) => onVisibleChanged(row, v)"
              />
            </template>
          </el-table-column>

          <el-table-column
            label="Editable"
            width="120"
            align="center"
          >
            <template #default="{ row }">
              <el-switch
                v-model="row.editable"
                :disabled="!row.visible || row.moduleKey === 'configuracion-general'"
              />
            </template>
          </el-table-column>
        </el-table>
      </el-form>

      <template #footer>
        <el-button @click="createModalVisible = false"> Cancelar </el-button>
        <el-button
          type="primary"
          @click="saveRole"
        >
          Guardar
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRolStore } from '@/stores/rol.js';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconAdd from '~icons/mdi/add';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomSwitch from '@/shared/components/custom_switch.vue';

const rolStore = useRolStore();
const { rolList } = storeToRefs(rolStore);
const { firstRol, createRolHTTP, deleteRolHTTP, updateRolHTTP, findRolByCodigoHTTP } = rolStore;

const mode = ref('create');
const editingCodigo = ref(null);
const createModalVisible = ref(false);

const roleFormRef = ref(null);

const roleRules = {
  Descripcion: [
    { required: true, message: 'Ingresar nombre del rol', trigger: 'blur' },
    {
      validator: (_, val, cb) => {
        if (!String(val ?? '').trim()) return cb(new Error('Ingresar nombre del rol'));
        cb();
      },
      trigger: 'blur',
    },
  ],
};

const newRole = ref({
  Descripcion: '',
  Activo: true,
});

const modulesMatrix = ref([
  { moduleKey: 'prospectos', label: 'Prospectos', visible: false, editable: false },
  { moduleKey: 'solicitud', label: 'Solicitud', visible: false, editable: false },
  { moduleKey: 'cotizacion', label: 'Cotización', visible: false, editable: false },
  { moduleKey: 'propuestas', label: 'Propuestas', visible: false, editable: false },
  { moduleKey: 'especificacion-tecnica', label: 'ET', visible: false, editable: false },
  { moduleKey: 'orden-produccion', label: 'OT', visible: false, editable: false },
  { moduleKey: 'recursos', label: 'Recursos', visible: false, editable: false },
  { moduleKey: 'cilindros', label: 'Cilindros', visible: false, editable: false },
  { moduleKey: 'suajes', label: 'Suajes', visible: false, editable: false },
  { moduleKey: 'materiales', label: 'Materiales', visible: false, editable: false },
  { moduleKey: 'usuarios', label: 'Usuarios', visible: false, editable: false },
  { moduleKey: 'configuracion-general', label: 'Configuración', visible: false, editable: false },
  {
    moduleKey: 'configuraciones-por-defecto',
    label: 'Configuraciones por defecto',
    visible: false,
    editable: false,
  },
  { moduleKey: 'movimientos-wip', label: 'Movimientos WIP', visible: false, editable: false },
  // (COTI-594): visible en true para que aparezca marcado por defecto al crear un rol.
  { moduleKey: 'traslado-stock', label: 'Traslado de Stock', visible: true, editable: false },
  { moduleKey: 'monitor-tv', label: 'Monitor TV', visible: false, editable: false },
  { moduleKey: 'stock', label: 'Movimientos de inventarios', visible: false, editable: false },
  { moduleKey: 'proveedor', label: 'Proveedores', visible: false, editable: false },
  { moduleKey: 'agente', label: 'Agentes', visible: false, editable: false },
  { moduleKey: 'tipo-tratado', label: 'Tipos de Tratado', visible: false, editable: false },
  { moduleKey: 'colores', label: 'Colores', visible: false, editable: false },
  { moduleKey: 'reportes', label: 'Reportes', visible: false, editable: false },
  { moduleKey: 'cotizacion_costos', label: 'Cotización - Costos', visible: false, editable: false },
]);

const openCreateModal = () => {
  resetForm();
  mode.value = 'create';
  editingCodigo.value = null;
  createModalVisible.value = true;
};

const resetForm = () => {
  newRole.value = { Descripcion: '', Activo: true };
  modulesMatrix.value.forEach((m) => {
    m.visible = false;
    m.editable = false;
  });
};

const saveRole = async () => {
  const ok = await roleFormRef.value?.validate?.().catch(() => false);
  if (!ok) return;

  newRole.value.Descripcion = String(newRole.value.Descripcion || '').trim();

  const payload = {
    ...newRole.value,
    modules: modulesMatrix.value.map((m) => ({
      moduleKey: m.moduleKey,
      visible: m.visible,
      editable: m.editable,
    })),
  };

  if (mode.value === 'edit') {
    await updateRolHTTP(editingCodigo.value, payload);
  } else {
    await createRolHTTP(payload);
  }

  await firstRol();
  createModalVisible.value = false;
};

const editRole = async (row) => {
  if (row.EsSistema) return;

  mode.value = 'edit';
  editingCodigo.value = row.Codigo;
  createModalVisible.value = true;

  const full = await findRolByCodigoHTTP(row.Codigo);

  newRole.value = {
    Descripcion: full.Descripcion ?? '',
    Activo: full.Activo ?? true,
  };

  applyPermissionsToMatrix(full.RolPermiso || []);
};

const applyPermissionsToMatrix = (rolPermiso) => {
  const codes = new Set((rolPermiso || []).map((rp) => rp.CodigoPermiso));

  modulesMatrix.value.forEach((m) => {
    const module = m.moduleKey;

    const hasListOrRead = codes.has(`${module}:list`) || codes.has(`${module}:read`);

    const hasAnyEdit =
      codes.has(`${module}:create`) || codes.has(`${module}:edit`) || codes.has(`${module}:delete`);

    if (module === 'configuracion-general') {
      const enabled = hasListOrRead || hasAnyEdit;
      m.visible = enabled;
      m.editable = enabled;
      return;
    }

    m.visible = hasListOrRead || hasAnyEdit;
    m.editable = hasAnyEdit;
  });
};
const onVisibleChanged = (row, v) => {
  if (row.moduleKey === 'configuracion-general') {
    row.editable = !!v;
    return;
  }
  if (!v) row.editable = false;
};
const deleteRole = async (row) => {
  await deleteRolHTTP(row.Codigo);
};

onMounted(async () => {
  await firstRol();
});
</script>
